import { useState, useRef, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Spinner from '../components/Spinner';
import Footer from '../components/Footer';
import 'altcha';  // Register the web component
import { ALTCHA_CHALLENGE_URL, ALTCHA_ENABLED, HIKE_PAGE_ONLY, LOGIN_ADMIN_ONLY } from '../config';

export default function Register({ onSwitchToLogin }) {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    nome: '',
    cognome: '',
    email: '',
    password: '',
    numero_telefono: '',
    data_nascita: '',
    privacy_consent: false,
    website: ''  // Honeypot field - must stay empty
  });
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [altchaPayload, setAltchaPayload] = useState(null);
  const [altchaError, setAltchaError] = useState(null);
  const altchaRef = useRef(null);
  const isSubmittedRef = useRef(false); // Track successful submission to prevent error display
  const { register } = useAuth();

  // Handle ALTCHA widget events
  useEffect(() => {
    const widget = altchaRef.current;
    if (!widget) return;

    const handleStateChange = (ev) => {
      if (ev.detail) {
        if (ev.detail.state === 'verified') {
          setAltchaPayload(ev.detail.payload);
          setAltchaError(null);
        } else if (ev.detail.state === 'error') {
          setAltchaPayload(null);
          setAltchaError('Errore verifica CAPTCHA. Riprova.');
        } else if (ev.detail.state === 'unverified') {
          setAltchaPayload(null);
        }
      }
    };

    widget.addEventListener('statechange', handleStateChange);
    return () => widget.removeEventListener('statechange', handleStateChange);
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validate privacy consent
    if (!formData.privacy_consent) {
      setError('Devi accettare l\'informativa sulla privacy per registrarti');
      return;
    }

    // Validate ALTCHA (only if enabled)
    if (ALTCHA_ENABLED && !altchaPayload) {
      setError('Completa la verifica CAPTCHA');
      return;
    }

    setIsLoading(true);

    try {
      const registrationData = {
        ...formData,
        altcha_payload: altchaPayload
      };
      const email = await register(registrationData);
      // Mark as successfully submitted to prevent error display during navigation
      isSubmittedRef.current = true;
      // Registration successful - navigate to verification prompt page
      navigate('/verify-email-prompt', { state: { email } });
    } catch (err) {
      // Don't show errors if form was already successfully submitted
      // (can happen due to race conditions during navigation)
      if (isSubmittedRef.current) return;

      // Only show errors that are not caused by navigation/abort
      // "NetworkError" can occur when page navigates during pending requests
      if (err.name !== 'AbortError' && !err.message?.includes('NetworkError')) {
        setError(err.message);
      }
      setIsLoading(false);
      // Reset ALTCHA widget on error
      setAltchaPayload(null);
      // The widget will automatically refetch a new challenge
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 flex flex-col p-4">
      <div className="flex-1 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-xl p-8 w-full max-w-md">
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => navigate('/')}
            className="text-gray-400 hover:text-gray-600 transition"
            title="Torna alla home"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
          </button>
          <h2 className="text-3xl font-bold text-gray-800 text-center flex-1">Registrazione</h2>
          <div className="w-5" />
        </div>
        
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nome
              </label>
              <input
                type="text"
                name="nome"
                value={formData.nome}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Cognome
              </label>
              <input
                type="text"
                name="cognome"
                value={formData.cognome}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Password
            </label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                name="password"
                value={formData.password}
                onChange={handleChange}
                className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                required
                minLength={6}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
              >
                {showPassword ? (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                )}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Numero di telefono
            </label>
            <input
              type="tel"
              name="numero_telefono"
              value={formData.numero_telefono}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Data di nascita
            </label>
            <input
              type="date"
              name="data_nascita"
              value={formData.data_nascita}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
            />
          </div>

          {/* Privacy consent checkbox - required for GDPR */}
          <div className="flex items-start">
            <input
              type="checkbox"
              name="privacy_consent"
              id="privacy_consent"
              checked={formData.privacy_consent}
              onChange={handleChange}
              className="w-4 h-4 mt-0.5 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
              style={{ accentColor: '#0E60C6' }}
            />
            <label htmlFor="privacy_consent" className="ml-2 text-sm text-gray-700">
              Ho letto e accetto l'<Link to="/privacy" target="_blank" className="font-medium underline" style={{ color: '#0E60C6' }}>Informativa sulla Privacy</Link> *
            </label>
          </div>

          {/* Honeypot field - hidden from real users, bots will fill it */}
          <div aria-hidden="true" style={{ position: 'absolute', left: '-9999px', top: '-9999px', opacity: 0, height: 0, overflow: 'hidden' }}>
            <label htmlFor="website">Website</label>
            <input
              type="text"
              name="website"
              id="website"
              value={formData.website}
              onChange={handleChange}
              tabIndex={-1}
              autoComplete="off"
            />
          </div>

          {/* ALTCHA Widget - Self-hosted CAPTCHA */}
          {ALTCHA_ENABLED && ALTCHA_CHALLENGE_URL && (
            <div className="flex flex-col items-center gap-2">
              <altcha-widget
                ref={altchaRef}
                challengeurl={ALTCHA_CHALLENGE_URL}
                hidefooter
                strings={JSON.stringify({
                  label: "Non sono un robot",
                  verifying: "Verifica in corso...",
                  verified: "Verificato",
                  error: "Verifica fallita. Riprova.",
                  expired: "Verifica scaduta. Riprova.",
                  waitAlert: "Verifica in corso... attendere."
                })}
              />
              {altchaError && (
                <p className="text-red-500 text-sm">{altchaError}</p>
              )}
            </div>
          )}

          <button
            type="submit"
            disabled={isLoading}
            className="w-full text-white py-2 rounded-lg transition font-medium flex items-center justify-center gap-2"
            style={{ backgroundColor: isLoading ? '#93C5FD' : '#0E60C6' }}
            onMouseEnter={(e) => !isLoading && (e.currentTarget.style.backgroundColor = '#0B4FA3')}
            onMouseLeave={(e) => !isLoading && (e.currentTarget.style.backgroundColor = '#0E60C6')}
          >
            {isLoading && <Spinner size="md" color="white" />}
            {isLoading ? 'Registrazione in corso...' : 'Registrati'}
          </button>
        </form>

        {!HIKE_PAGE_ONLY && !LOGIN_ADMIN_ONLY && (
          <p className="mt-4 text-center text-sm text-gray-600">
            Hai già un account?{' '}
            <button
              onClick={onSwitchToLogin}
              className="font-medium"
              style={{ color: '#0E60C6' }}
            >
              Accedi
            </button>
          </p>
        )}
        </div>
      </div>

      <Footer />
    </div>
  );
}